import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, CSP_NONCE, Component, computed, Inject, input, OnDestroy, Optional, Renderer2, signal } from '@angular/core';
import { NgxHasHeight } from '../ngx-has-height';
import { VerbosityLevel } from '../options/verbosity-level';
import { PdfCspPolicyService } from '../pdf-csp-policy.service';
import { PdfBreakpoints } from '../responsive-visibility';
import { UnitToPx } from '../unit-to-px';

@Component({
    selector: 'pdf-dynamic-css',
    templateUrl: './dynamic-css.component.html',
    styleUrls: ['./dynamic-css.component.css'],
    standalone: false // NOSONAR — intentional for backward compatibility with NgModule consumers
})
export class DynamicCssComponent implements OnDestroy {
  public zoom = input<number>(1.0);

  public width = input<number>(3.14159265359);

  public xxs = signal(455);

  public xs = signal(490);

  public sm = signal(560);

  public md = signal(610);

  public lg = signal(660);

  public xl = signal(740);

  public xxl = signal(830);

  public xxxl = signal(1000);

  public toolbarWidth = signal(0);

  public style = computed(() => {
    return `
#toolbarContainer .always-in-secondary-menu {
  display: none;
}

/* #3105 fixed the layout of the find bar */
.findbar {
  max-width: ${this.toolbarWidth() > 0 ? this.toolbarWidth() + 'px' : '100%'};
}
/* #3105 end of fix */

#secondaryToolbar .always-in-secondary-menu {
  display: inline-flex;
}

#outerContainer #mainContainer .visibleXXSView,
#outerContainer #mainContainer .visibleTinyView,
#outerContainer #mainContainer .visibleSmallView,
#outerContainer #mainContainer .visibleMediumView,
#outerContainer #mainContainer .visibleLargeView,
#outerContainer #mainContainer .visibleXLView,
#outerContainer #mainContainer .visibleXXLView,
#outerContainer #mainContainer .visibleXXXLView {
  display: none;
}

.pdf-margin-top-3px {
  margin-top: 3px;
}

.pdf-margin-top--2px {
  margin-top: -2px;
}

@media all and (max-width: ${this.xxl()}) {
  #sidebarContent {
    background-color: rgba(0, 0, 0, 0.7);
  }

  html[dir='ltr'] #outerContainer.sidebarOpen #viewerContainer {
    left: 0px !important;
  }
  html[dir='rtl'] #outerContainer.sidebarOpen #viewerContainer {
    right: 0px !important;
  }

  #outerContainer .hiddenLargeView,
  #outerContainer .hiddenMediumView {
    display: inherit;
  }
}

@media all and (max-width: ${this.lg()}px) {
  .toolbarButtonSpacer {
    width: 15px;
  }

  #outerContainer .hiddenLargeView {
    display: none;
  }
  #outerContainer  #mainContainer .visibleLargeView {
    display: inherit;
  }
}

@media all and (max-width: ${this.md()}px) {
  .toolbarButtonSpacer {
    display: none;
  }
  #outerContainer .hiddenMediumView {
    display: none;
  }
  #outerContainer  #mainContainer .visibleMediumView {
    display: inherit;
  }
}

@media all and (max-width: ${this.sm()}px) {
  #outerContainer .hiddenSmallView,
  #outerContainer .hiddenSmallView * {
    display: none;
  }
  #outerContainer  #mainContainer .visibleSmallView {
    display: inherit;
  }
  .toolbarButtonSpacer {
    width: 0;
  }
  html[dir='ltr'] .findbar {
    left: 38px;
  }
  html[dir='rtl'] .findbar {
    right: 38px;
  }
}

#outerContainer .visibleXLView,
#outerContainer .visibleXXLView,
#outerContainer .visibleXXXLView,
#outerContainer .visibleTinyView {
  display: none;
}

@media all and (max-width: ${this.xl()}px) {
  #outerContainer .hiddenXLView {
    display: none;
  }
  #outerContainer #mainContainer .visibleXLView {
    display: inherit;
  }
}

@media all and (max-width: ${this.xxxl()}px) {
  #outerContainer .hiddenXXXLView {
    display: none;
  }
  #outerContainer  #mainContainer .visibleXXXLView {
    display: inherit;
  }
}

@media all and (max-width: ${this.xxl()}px) {
  #outerContainer .hiddenXXLView {
    display: none;
  }
  #outerContainer  #mainContainer .visibleXXLView {
    display: inherit;
  }
}

@media all and (max-width: ${this.xs()}px) {
  #outerContainer .hiddenTinyView,
  #outerContainer .hiddenTinyView * {
    display: none;
  }
  #outerContainer  #mainContainer .visibleTinyView {
    display: inherit;
  }
}

@media all and (max-width: ${this.xxs()}px) {
  #outerContainer .hiddenXXSView,
  #outerContainer .hiddenXXSView * {
    display: none;
  }
  #outerContainer #mainContainer .visibleXXSView {
    display: inherit;
  }
}
  `;
  });

  constructor(
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly pdfCspPolicyService: PdfCspPolicyService,
    @Inject(CSP_NONCE) @Optional() private readonly nonce: string | null | undefined,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Width is now an input signal and will be provided by the parent component
  }

  private isZoneless(): boolean {
    const Zone = (globalThis as any).Zone;
    return typeof Zone === 'undefined' || !Zone?.current;
  }

  private asyncWithCD(callback: () => void): () => void {
    return () => {
      callback();
      if (this.isZoneless()) {
        this.cdr.detectChanges();
      }
    };
  }

  public updateToolbarWidth() {
    const container = document.getElementById('toolbarViewer') ?? document.getElementById('outerContainer');
    if (!container) {
      return;
    }
    const toolbarWidthInPixels = container.clientWidth;

    // #3105 fixed the layout of the find bar
    // Store the toolbar width for findbar max-width calculation
    this.toolbarWidth.set(toolbarWidthInPixels);
    // #3105 end of fix

    const fullWith = this.document.body.clientWidth;
    const partialViewScale = fullWith / toolbarWidthInPixels;
    const scaleFactor = partialViewScale * (this.zoom() ? this.zoom() : 1);

    this.xs.set(scaleFactor * PdfBreakpoints.xs);
    this.sm.set(scaleFactor * PdfBreakpoints.sm);
    this.md.set(scaleFactor * PdfBreakpoints.md);
    this.lg.set(scaleFactor * PdfBreakpoints.lg);
    this.xl.set(scaleFactor * PdfBreakpoints.xl);
    this.xxl.set(scaleFactor * PdfBreakpoints.xxl);
    this.xxxl.set(scaleFactor * PdfBreakpoints.xxxl);

    let styles = this.document.getElementById('pdf-dynamic-css') as HTMLStyleElement;
    if (!styles) {
      styles = this.document.createElement('STYLE') as HTMLStyleElement;
      styles.id = 'pdf-dynamic-css';
      this.pdfCspPolicyService.addTrustedCSS(styles, this.style());

      if (this.nonce) {
        styles.nonce = this.nonce;
      }

      this.renderer.appendChild(this.document.head, styles);
    } else {
      this.pdfCspPolicyService.addTrustedCSS(styles, this.style());
    }
  }

  public removeScrollbarInInfiniteScrollMode(
    restoreHeight: boolean,
    pageViewMode: string,
    primaryMenuVisible: boolean,
    ngxExtendedPdfViewer: NgxHasHeight,
    logLevel: VerbosityLevel
  ): void {
    if (pageViewMode === 'infinite-scroll' || restoreHeight) {
      const viewer = document.getElementById('viewer');
      const zoom = document.getElementsByClassName('zoom')[0];
      if (viewer) {
        setTimeout(this.asyncWithCD(() => {
          if (pageViewMode === 'infinite-scroll') {
            const height = viewer.clientHeight + 17;
            if (primaryMenuVisible) {
              ngxExtendedPdfViewer.height = height + 35 + 'px';
            } else if (height > 17) {
              ngxExtendedPdfViewer.height = height + 'px';
            } else if (ngxExtendedPdfViewer.height === undefined) {
              ngxExtendedPdfViewer.height = '100%';
            }
            if (zoom) {
              (<HTMLElement>zoom).style.height = ngxExtendedPdfViewer.height;
            }
          } else if (restoreHeight) {
            // Clear both height and minHeight so Angular's [style.height] binding
            // re-evaluates. Don't directly set zoom.style.height — that bypasses
            // Angular's change detection and causes the binding to go stale.
            ngxExtendedPdfViewer.height = undefined;
            ngxExtendedPdfViewer.minHeight = undefined;
            ngxExtendedPdfViewer.autoHeight = true;
            ngxExtendedPdfViewer.markForCheck();
            // checkHeight will recalculate and set minHeight to the correct value
            this.checkHeight(ngxExtendedPdfViewer, logLevel);
          }
        }));
      }
    }
  }

  public checkHeight(ngxExtendedPdfViewer: NgxHasHeight, logLevel: VerbosityLevel): void {
    if (this.isHeightDefinedWithUnits(ngxExtendedPdfViewer.height)) return;
    if (this.isPrinting()) return;

    const container = this.getContainer();
    if (!container) return;

    if (this.isContainerHeightZero(container, ngxExtendedPdfViewer, logLevel)) {
      ngxExtendedPdfViewer.autoHeight = true;
    }

    if (ngxExtendedPdfViewer.autoHeight) {
      this.adjustHeight(container, ngxExtendedPdfViewer);
    }
  }

  /**
   * The height is defined with one of the units vh, vw, em, rem, etc.
   * So the height check isn't necessary.
   * @param height the height of the container
   */
  private isHeightDefinedWithUnits(height: string | undefined): boolean {
    return height ? Number.isNaN(Number(height.replace('%', ''))) : false;
  }

  /**
   * #1702 workaround to a Firefox bug: when printing, container.clientHeight is temporarily 0,
   * causing ngx-extended-pdf-viewer to default to 100 pixels height. So it's better to do nothing.
   * @returns true if data-pdfjsprinting is set
   */
  private isPrinting(): boolean {
    /* istanbul ignore next -- SSR guard, untestable in JSDOM */
    if (!this.isBrowser()) {
      return false;
    }
    return !!document.querySelector('[data-pdfjsprinting]');
  }

  /**
   * Checks if the code is running in a browser environment.
   */
  private isBrowser(): boolean {
    return typeof globalThis.window !== 'undefined' && typeof document !== 'undefined';
  }

  private getContainer(): HTMLElement | null {
    return typeof document !== 'undefined' ? (document.getElementsByClassName('zoom')[0] as HTMLElement) : null;
  }

  private isContainerHeightZero(container: HTMLElement, ngxExtendedPdfViewer: NgxHasHeight, logLevel: VerbosityLevel): boolean {
    if (container.clientHeight === 0) {
      if (logLevel >= VerbosityLevel.WARNINGS && !ngxExtendedPdfViewer.autoHeight && ngxExtendedPdfViewer.height !== '100%') {
        console.warn(
          "The height of the PDF viewer widget is zero pixels. Please check the height attribute. Is there a syntax error? Or are you using a percentage with a CSS framework that doesn't support this? The height is adjusted automatedly."
        );
      }
      return true;
    }
    return false;
  }

  private adjustHeight(container: HTMLElement, ngxExtendedPdfViewer: NgxHasHeight): void {
    const availableBottom = this.findAvailableBottom(container);
    const rect = container.getBoundingClientRect();
    const top = rect.top;
    let maximumHeight = availableBottom - top;
    const padding = this.calculateBorderMargin(container);
    maximumHeight -= padding;
    ngxExtendedPdfViewer.minHeight = maximumHeight > 100 ? `${maximumHeight}px` : '100px';
    ngxExtendedPdfViewer.markForCheck();
  }

  /**
   * Walks up the DOM to find the nearest ancestor with constrained overflow (hidden/auto/scroll)
   * and an explicit height. This ensures the viewer respects parent container bounds
   * (e.g., a mat-card-content with height: 80vh) instead of always using the full viewport.
   * Falls back to window.innerHeight if no constraining ancestor is found.
   */
  private findAvailableBottom(element: HTMLElement): number {
    let parent = element.parentElement;
    while (parent && parent !== document.body && parent !== document.documentElement) {
      const style = globalThis.window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if ((overflowY === 'hidden' || overflowY === 'auto' || overflowY === 'scroll')
          && style.height !== 'auto' && style.height !== '') {
        return parent.getBoundingClientRect().bottom;
      }
      parent = parent.parentElement;
    }
    return globalThis.window.innerHeight;
  }

  private calculateBorderMargin(container: HTMLElement | null): number {
    if (container) {
      const computedStyle = globalThis.window.getComputedStyle(container);

      const padding = UnitToPx.toPx(computedStyle.paddingBottom);
      const margin = UnitToPx.toPx(computedStyle.marginBottom);
      if (container.style.zIndex) {
        return padding + margin;
      }
      return padding + margin + this.calculateBorderMargin(container.parentElement);
    }
    return 0;
  }

  public ngOnDestroy() {
    const styles = this.document.getElementById('pdf-dynamic-css') as HTMLElement;
    if (styles?.parentElement) {
      styles.remove();
    }
  }
}
